import { useEffect, useMemo, useState } from 'react'

import { traceService } from '../services/traceService'

type TenantOption = {
  id: string
  name: string
  slug?: string
}

type SearchLogSummary = {
  days: number
  from: string
  total: number
  withResults: number
  successRate: number
  strategies: Array<{ strategy: string; count: number }>
}

type FailingQueryRow = {
  originalQuery: string
  freq: number
  avgResults: number
  avgReformulations: number
}

export default function SearchLogPage() {
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [selectedTenantId, setSelectedTenantId] = useState('')
  const [days, setDays] = useState(7)

  const [summary, setSummary] = useState<SearchLogSummary | null>(null)
  const [failingQueries, setFailingQueries] = useState<FailingQueryRow[]>([])

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId) || null,
    [tenants, selectedTenantId]
  )

  useEffect(() => {
    const loadTenants = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await traceService.getTenants()
        setTenants(response.data)
        setSelectedTenantId((current) => current || response.data[0]?.id || '')
      } catch (err) {
        setError('Failed to load tenants')
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    void loadTenants()
  }, [])

  const fetchSearchLogData = async (tenantId: string, nextDays: number) => {
    if (!tenantId) {
      setSummary(null)
      setFailingQueries([])
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const [summaryRes, failingRes] = await Promise.all([
        traceService.getSearchLogSummary({ tenantId, days: nextDays }),
        traceService.getSearchFailingQueries({ tenantId, days: nextDays, limit: 20 }),
      ])

      setSummary(summaryRes.data)
      setFailingQueries(failingRes.data)
    } catch (err) {
      setError('Failed to load search log')
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!selectedTenantId) return
    void fetchSearchLogData(selectedTenantId, days)
  }, [selectedTenantId, days])

  return (
    <div className="p-4">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-xl font-semibold text-slate-900">SearchLog</div>
          <div className="text-sm text-slate-600">Éxito de queries y candidatos para sinónimos/reformulaciones.</div>
        </div>

        <button
          className="rounded bg-slate-900 px-3 py-2 text-sm font-semibold text-white"
          onClick={() => void fetchSearchLogData(selectedTenantId, days)}
          disabled={isLoading || !selectedTenantId}
        >
          Actualizar
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tenant</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              disabled={isLoading || tenants.length === 0}
            >
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name} · {tenant.id}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ventana</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value={1}>1 día</option>
              <option value={7}>7 días</option>
              <option value={14}>14 días</option>
              <option value={30}>30 días</option>
            </select>
          </div>

          <div className="border rounded-lg p-4 bg-slate-50">
            <div className="text-xs text-slate-600">Tenant seleccionado</div>
            <div className="font-semibold text-slate-900">{selectedTenant?.name || '-'}</div>
            <div className="text-xs text-slate-500">{selectedTenant?.id || ''}</div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-600">Cargando...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-xs text-slate-500">Total logs</div>
              <div className="text-2xl font-semibold text-slate-900">{summary?.total ?? 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-xs text-slate-500">Con resultados</div>
              <div className="text-2xl font-semibold text-slate-900">{summary?.withResults ?? 0}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-xs text-slate-500">Success rate</div>
              <div className="text-2xl font-semibold text-slate-900">
                {summary ? `${Math.round((summary.successRate || 0) * 100)}%` : '0%'}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-semibold text-slate-900">Estrategias</div>
              <div className="text-xs text-slate-500">Top 10</div>
            </div>
            {(summary?.strategies || []).length === 0 ? (
              <div className="text-sm text-slate-600">Sin datos.</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {(summary?.strategies || []).map((s) => (
                  <div key={s.strategy} className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">{s.strategy}</div>
                    <div className="text-lg font-semibold text-slate-900">{s.count}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="font-semibold text-slate-900">Queries frecuentes que siempre fallan</div>
              <div className="text-xs text-slate-500">avgResults = 0</div>
            </div>

            {failingQueries.length === 0 ? (
              <div className="text-sm text-slate-600">No hay queries fallidas frecuentes en esta ventana.</div>
            ) : (
              <div className="overflow-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-slate-600">
                      <th className="py-2 pr-3">Query</th>
                      <th className="py-2 pr-3">Freq</th>
                      <th className="py-2 pr-3">Avg reformulations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failingQueries.map((row) => (
                      <tr key={row.originalQuery} className="border-t">
                        <td className="py-2 pr-3 font-mono">{row.originalQuery}</td>
                        <td className="py-2 pr-3">{row.freq}</td>
                        <td className="py-2 pr-3">{row.avgReformulations.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
